/**
 * 🔤 Unterfächer je Buchstabe (Sortierung nach Nachname).
 *
 * Große Buchstaben (B, S, V …) brauchen mehrere Regalfächer. Statt „BA / BE / BI / BO / BU", bei
 * denen Mitarbeiter überlegen müssen, wohin „Brandon" gehört, vergibt die App eine Nummer:
 * der ZWEITE Buchstabe des Nachnamens bestimmt das Unterfach, auf dem Etikett steht fertig „B4".
 *
 * Gespeichert werden je Buchstabe die Anfangsbuchstaben der Unterfächer 2…n (Unterfach 1 beginnt
 * immer bei „a"): 'B:f,k,p,u' = B1 a–e, B2 f–j, B3 k–o, B4 p–t, B5 u–z. So bleibt die Aufteilung
 * stabil, egal ob sie gleichmäßig nach Alphabet oder nach der echten Paketmenge gewählt wurde.
 *
 * Ohne Einstellung (alle bisherigen Geräte) bleibt das Fach der reine Buchstabe.
 */
import { splitSurname } from './printerService';

const KEY = 'kiosk_regal_sub';
export const MAX_UNTERFAECHER = 20;
const ABC = 'abcdefghijklmnopqrstuvwxyz';

/** 'B:f,k,p,u;S:j' → { B: ['f','k','p','u'], S: ['j'] } (Ungültiges wird verworfen) */
export function parseSub(text) {
  const out = {};
  if (typeof text !== 'string' || !text || text === 'keine') return out;
  for (const teil of text.split(';')) {
    const m = /^([A-Z]):([b-z](?:,[b-z])*)$/.exec(teil);
    if (!m) continue;
    const grenzen = [...new Set(m[2].split(','))].sort();
    if (grenzen.length >= 1 && grenzen.length <= MAX_UNTERFAECHER - 1) out[m[1]] = grenzen;
  }
  return out;
}

export function formatSub(map) {
  const teile = Object.keys(map || {}).sort()
    .filter((b) => /^[A-Z]$/.test(b) && Array.isArray(map[b]) && map[b].length)
    .map((b) => `${b}:${[...new Set(map[b])].sort().join(',')}`);
  return teile.length ? teile.join(';') : 'keine';
}

export function getSubCfg() {
  try { return parseSub(localStorage.getItem(KEY)); } catch { return {}; }
}
export function setSubCfg(map) {
  try { localStorage.setItem(KEY, formatSub(map)); } catch { /* */ }
}

/** n Unterfächer mit (fast) gleich vielen Buchstaben; der Rest geht auf die letzten. 5 → f,k,p,u */
export function gleichmaessig(n) {
  const k = Math.max(1, Math.min(MAX_UNTERFAECHER, parseInt(n, 10) || 1));
  const basis = Math.floor(26 / k), rest = 26 - basis * k;
  const grenzen = [];
  let start = 0;
  for (let g = 1; g < k; g++) { start += basis + (g > k - rest ? 1 : 0); grenzen.push(ABC[start]); }
  return grenzen;
}

/**
 * n Unterfächer so schneiden, dass in jedem möglichst gleich viele PAKETE liegen (zusammenhängende
 * Buchstabenbereiche; kleinste Summe der Quadrate = so gleichmäßig wie möglich).
 * @param {Object<string, number>} anzahl  Pakete je zweitem Buchstaben, z. B. { a: 10, e: 15, r: 11 }
 */
export function ausbalanciert(anzahl, n) {
  const k = Math.max(1, Math.min(MAX_UNTERFAECHER, parseInt(n, 10) || 1));
  if (k === 1) return [];
  const c = [...ABC].map((b) => Math.max(0, Number((anzahl || {})[b]) || 0));
  const gesamt = c.reduce((a, b) => a + b, 0);
  if (!gesamt) return gleichmaessig(k);
  const pre = [0];
  for (const v of c) pre.push(pre[pre.length - 1] + v);
  const summe = (i, j) => pre[j] - pre[i]; // Buchstaben i..j-1
  // best[g][j] = kleinste Quadratsumme, wenn die ersten j Buchstaben auf g Unterfächer verteilt sind
  const best = Array.from({ length: k + 1 }, () => Array(27).fill(Infinity));
  const schnitt = Array.from({ length: k + 1 }, () => Array(27).fill(0));
  best[0][0] = 0;
  for (let g = 1; g <= k; g++) {
    for (let j = g; j <= 26 - (k - g); j++) {
      for (let i = g - 1; i < j; i++) {
        const wert = best[g - 1][i] + summe(i, j) ** 2;
        // bei Gleichstand: Schnitt bevorzugen, der die Buchstaben gleichmäßiger verteilt
        if (wert < best[g][j] || (wert === best[g][j] && Math.abs((j - i) - 26 / k) < Math.abs((j - schnitt[g][j]) - 26 / k))) {
          best[g][j] = wert; schnitt[g][j] = i;
        }
      }
    }
  }
  const grenzen = [];
  let j = 26;
  for (let g = k; g > 1; g--) { j = schnitt[g][j]; grenzen.unshift(ABC[j]); }
  return grenzen;
}

/** Bereiche zum Anzeigen/Beschriften: [{ nr: 1, von: 'a', bis: 'e' }, …] */
export function bereiche(grenzen) {
  const g = [...new Set(grenzen || [])].sort();
  const starts = ['a', ...g];
  return starts.map((von, i) => ({ nr: i + 1, von, bis: i + 1 < starts.length ? ABC[ABC.indexOf(starts[i + 1]) - 1] : 'z' }));
}

/** Zweiter Buchstabe eines Nachnamens (ohne Akzente; Sonderzeichen übersprungen), '' wenn es keinen gibt */
export function zweiterBuchstabe(nachname) {
  const s = String(nachname || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '');
  return s.length > 1 ? s[1] : '';
}

/** Unterfach-Nummer für einen Buchstaben + zweiten Buchstaben; null = Buchstabe hat keine Unterfächer */
export function unterfach(buchstabe, zweiter, map) {
  const grenzen = (map || {})[String(buchstabe || '').toUpperCase()];
  if (!grenzen || !grenzen.length) return null;
  const z = zweiter || 'a';
  return 1 + grenzen.filter((g) => g <= z).length;
}

/**
 * Fach für einen Empfängernamen bei Nachnamen-Sortierung: „B" oder — mit Unterfächern — „B4".
 */
export function autoRegalFach(name, map = getSubCfg()) {
  const { surname, letter } = splitSurname(name);
  if (!letter) return '';
  const nr = unterfach(letter, zweiterBuchstabe(surname), map);
  return nr ? `${letter}${nr}` : letter;
}

/**
 * Wie viele Pakete je Anfangsbuchstabe und zweitem Buchstaben? (für „nach Paketmenge aufteilen")
 * @returns {Object<string, {gesamt: number, zweite: Object<string, number>}>}
 */
export function zaehleNachnamen(pakete) {
  const out = {};
  for (const p of (pakete || [])) {
    if (!p || !p.name) continue;
    const { surname, letter } = splitSurname(p.name);
    if (!/^[A-Z]$/.test(letter || '')) continue;
    const e = out[letter] || (out[letter] = { gesamt: 0, zweite: {} });
    const z = zweiterBuchstabe(surname) || 'a';
    e.gesamt += 1;
    e.zweite[z] = (e.zweite[z] || 0) + 1;
  }
  return out;
}
